import { useState, useEffect, useRef } from 'react';
import { ExternalLink, Pause, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAdminEdit } from '@/contexts/AdminEditContext';
import EditableText from './cms/EditableText';
import EditableImage from './cms/EditableImage';

const ReferencesSection = () => {
  const [isPaused, setIsPaused] = useState(false);
  const [isStarted, setIsStarted] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const [carouselApi, setCarouselApi] = useState<any>(null);
  const { t } = useLanguage();
  const { editMode } = useAdminEdit();

  const projects = [
    {
      titleKey: 'references.aiAgents',
      categoryKey: 'references.aiSolutions',
      image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=800&q=80",
      liveUrl: "https://dryvit.app.commodit.hu/"
    },
    {
      titleKey: 'references.corporateWebsite',
      categoryKey: 'references.webDevelopmentSeo',
      image: "/lovable-uploads/69148b2a-ac88-45e0-8bb4-4d9a94561c12.png",
      liveUrl: "https://sos-ajtoszerviz.hu/"
    },
    {
      titleKey: 'references.fizgo',
      categoryKey: 'references.softposAutomation',
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80",
      liveUrl: "https://fizgo.hu/"
    },
    {
      titleKey: 'references.innovationPlatform',
      categoryKey: 'references.itConsulting',
      image: "https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=800&q=80",
      liveUrl: "https://kissfightingacademy.com/"
    }
  ];

  // State to track current image URLs for each project
  const [currentImages, setCurrentImages] = useState<{[key: string]: string}>({});

  useEffect(() => {
    if (!editMode) {
      addAnimation();
    } else {
      setIsStarted(false);
    }
  }, [editMode]);

  // Auto-scroll for mobile carousel
  useEffect(() => {
    if (isMobile && carouselApi) {
      const interval = setInterval(() => {
        carouselApi.scrollNext();
      }, 3000);

      return () => clearInterval(interval);
    }
  }, [isMobile, carouselApi]);

  const addAnimation = () => {
    if (containerRef.current && scrollerRef.current && !editMode) {
      // Clear any existing duplicated items first
      const children = Array.from(scrollerRef.current.children);
      const originalChildren = children.slice(0, projects.length);
      
      // Remove any existing clones
      children.slice(projects.length).forEach(child => child.remove());

      // Clone the original items with updated images and event handlers
      originalChildren.forEach((item, index) => {
        const duplicatedItem = item.cloneNode(true) as HTMLElement;
        
         // Update image sources in the cloned node to match current state
         const images = duplicatedItem.querySelectorAll('img');
         images.forEach((img) => {
           // Use the same image key format as the main components
           const imageKey = `project_${index}`;
           if (currentImages[imageKey]) {
             img.src = currentImages[imageKey];
           }
         });
        
        // Re-attach event handlers for buttons in cloned elements
        const viewButton = duplicatedItem.querySelector('button[data-project-view]') as HTMLButtonElement;
        if (viewButton) {
          const projectUrl = projects[index]?.liveUrl;
          if (projectUrl) {
            viewButton.onclick = (e) => {
              e.stopPropagation();
              window.open(projectUrl, '_blank');
            };
          }
        }
        
        if (scrollerRef.current) {
          scrollerRef.current.appendChild(duplicatedItem);
        }
      });

      getDirection();
      getSpeed();
      setIsStarted(true);
    }
  };

  // Handle image updates from CMS
  const handleImageUpdate = (imageKey: string, newSrc: string) => {
    setCurrentImages(prev => ({ ...prev, [imageKey]: newSrc }));
  };

  // Re-run animation when images are updated to sync cloned elements
  useEffect(() => {
    if (isStarted && !isMobile && !editMode) {
      addAnimation();
    }
  }, [currentImages, isMobile, editMode, isStarted]);

  const getDirection = () => {
    if (containerRef.current) {
      containerRef.current.style.setProperty(
        "--animation-direction",
        "forwards"
      );
    }
  };

  const getSpeed = () => {
    if (containerRef.current) {
      containerRef.current.style.setProperty("--animation-duration", "35s");
    }
  };

  const togglePause = () => {
    setIsPaused(!isPaused);
  };

  return (
    <section id="references" className="py-24 px-4 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="float absolute top-40 left-20 w-24 h-24 bg-secondary/20 rounded-full blur-xl"></div>
        <div className="float absolute bottom-40 right-20 w-32 h-32 bg-accent/20 rounded-full blur-2xl" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="container mx-auto max-w-7xl relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-6xl font-display font-bold mb-6">
            <EditableText k="references.our" /> <EditableText k="references.works" className="gradient-text" />
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            <EditableText k="references.portfolioDesc" />
          </p>
        </div>

        {/* Desktop: Auto-play Control - Only show when not in edit mode */}
        {!isMobile && !editMode && (
          <div className="flex justify-center mb-6">
            <Button
              variant="outline"
              size="sm"
              onClick={togglePause}
              className="gap-2 border-primary/50 hover:bg-primary hover:text-primary-foreground transition-spring"
            >
              {isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
              {isPaused ? <EditableText k="references.play" /> : <EditableText k="references.pause" />} Auto-scroll
            </Button>
          </div>
        )}

{/* Conditional rendering based on device and edit mode */}
        {isMobile ? (
          /* Mobile: Stepped Carousel */
          <div className="relative px-4">
            <Carousel
              setApi={setCarouselApi}
              opts={{
                align: "center",
                loop: true,
              }}
              className="w-full max-w-sm mx-auto"
            >
              <CarouselContent className="-ml-2 md:-ml-4">
                {projects.map((project, index) => (
                  <CarouselItem key={`mobile-${index}`} className="pl-2 md:pl-4 basis-4/5">
                    <div 
                      className="relative bg-gradient-to-br from-card/80 to-card/40 backdrop-blur-md border border-border/30 rounded-2xl overflow-hidden active:scale-95 transition-all duration-200 cursor-pointer"
                      onClick={() => window.open(project.liveUrl, '_blank')}
                    >
                      {/* Project Image */}
                      <div className="relative aspect-[4/5] overflow-hidden bg-muted/10">
                         <EditableImage 
                           src={currentImages[`project_${index}`] || project.image} 
                           alt="Project screenshot"
                           className="w-full h-full object-cover object-center"
                           imageKey={`project_${index}`}
                           folder="projects"
                           onImageChange={(newSrc) => handleImageUpdate(`project_${index}`, newSrc)}
                         />
                        
                        {/* Gradient Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/30 to-transparent"></div>
                        
                        {/* Content Overlay */}
                        <div className="absolute bottom-0 left-0 right-0 p-4 z-10">
                          {/* Category Badge */}
                          <div className="inline-block text-xs font-semibold text-white bg-primary/90 backdrop-blur-md border border-primary/50 px-2 py-1 rounded-full mb-2 shadow-lg shadow-background/50 relative z-20">
                            <EditableText key={`mobile-cat-${index}`} k={project.categoryKey} as="span" />
                          </div>

                          {/* Title */}
                          <h3 className="text-lg font-display font-bold text-white mb-2 relative z-20">
                            <EditableText key={`mobile-title-${index}`} k={project.titleKey} as="span" />
                          </h3>

                          {/* Tap to view indicator */}
                          <div className="flex items-center gap-2 text-white/80 text-sm relative z-20">
                            <ExternalLink className="w-3 h-3" />
                            <EditableText key={`mobile-tap-${index}`} k="references.tapToView" as="span" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="left-2" />
              <CarouselNext className="right-2" />
            </Carousel>
          </div>
        ) : editMode ? (
          /* Desktop: Static Grid for Edit Mode */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 py-4">
            {projects.map((project, index) => (
              <div
                key={`edit-${index}`}
                className="w-full"
              >
                <div className="relative group bg-gradient-to-br from-card/80 to-card/40 backdrop-blur-md border border-border/30 rounded-2xl overflow-hidden hover:border-primary/40 transition-all duration-500 hover:shadow-2xl hover:shadow-primary/10">
                  {/* Project Image */}
                  <div className="relative aspect-[5/4] overflow-hidden bg-muted/10">
                       <EditableImage 
                         src={currentImages[`project_${index}`] || project.image} 
                         alt="Project screenshot"
                         className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-110"
                         imageKey={`project_${index}`}
                         folder="projects"
                         onImageChange={(newSrc) => handleImageUpdate(`project_${index}`, newSrc)}
                       />
                    
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent pointer-events-none"></div>
                    
                    {/* Live Preview Button */}
                    <Button
                      variant="secondary"
                      size="sm"
                      className="absolute top-3 right-3 gap-2 bg-background/20 backdrop-blur-md border-white/20 text-white hover:bg-background/40 transition-all opacity-100 z-30"
                      onClick={() => window.open(project.liveUrl, '_blank')}
                      data-project-view="true"
                    >
                      <ExternalLink className="w-3 h-3" />
                      <EditableText key={`edit-view-${index}`} k="references.view" as="span" />
                    </Button>

                    {/* Content Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-4 pb-16 z-10 pointer-events-none">
                      {/* Category Badge */}
                      <div className="inline-block text-xs font-semibold text-white bg-primary/90 backdrop-blur-md border border-primary/50 px-3 py-1 rounded-full mb-3 shadow-lg shadow-background/50 relative pointer-events-auto">
                        <EditableText key={`edit-cat-${index}`} k={project.categoryKey} as="span" />
                      </div>

                      {/* Title */}
                      <h3 className="text-lg md:text-xl font-display font-bold text-white relative pointer-events-auto">
                        <EditableText key={`edit-title-${index}`} k={project.titleKey} as="span" />
                      </h3>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Desktop: Infinite Moving Cards */
          <div
            ref={containerRef}
            className="relative z-20 max-w-7xl overflow-hidden [mask-image:linear-gradient(to_right,transparent,white_20%,white_80%,transparent)]"
          >
            <div
              ref={scrollerRef}
              className={`flex w-max min-w-full shrink-0 flex-nowrap gap-6 py-4 ${
                isStarted ? 'animate-scroll' : ''
              } ${isPaused ? '[animation-play-state:paused]' : ''}`}
            >
              {projects.map((project, index) => (
                <div
                  key={`scroll-${index}`}
                  className="w-[400px] max-w-full shrink-0"
                >
                  <div className="relative group bg-gradient-to-br from-card/80 to-card/40 backdrop-blur-md border border-border/30 rounded-2xl overflow-hidden hover:border-primary/40 transition-all duration-500 hover:shadow-2xl hover:shadow-primary/10">
                    {/* Project Image */}
                    <div className="relative aspect-[5/4] overflow-hidden bg-muted/10">
                         <EditableImage 
                           src={currentImages[`project_${index}`] || project.image} 
                           alt="Project screenshot"
                           className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-110"
                           imageKey={`project_${index}`}
                           folder="projects"
                           onImageChange={(newSrc) => handleImageUpdate(`project_${index}`, newSrc)}
                         />
                      
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent"></div>
                      
                      {/* Live Preview Button */}
                      <Button
                        variant="secondary"
                        size="sm"
                        className="absolute top-3 right-3 gap-2 bg-background/20 backdrop-blur-md border-white/20 text-white hover:bg-background/40 transition-all opacity-0 group-hover:opacity-100 z-30"
                        onClick={() => window.open(project.liveUrl, '_blank')}
                        data-project-view="true"
                      >
                        <ExternalLink className="w-3 h-3" />
                        <EditableText key={`scroll-view-${index}`} k="references.view" as="span" />
                      </Button>

                      {/* Content Overlay */}
                      <div className="absolute bottom-0 left-0 right-0 p-4 z-50">
                        {/* Category Badge */}
                        <div className="inline-block text-xs font-semibold text-white bg-primary/90 backdrop-blur-md border border-primary/50 px-3 py-1 rounded-full mb-3 shadow-lg shadow-background/50 relative z-60 pointer-events-auto">
                          <EditableText key={`scroll-cat-${index}`} k={project.categoryKey} as="span" />
                        </div>

                        {/* Title */}
                        <h3 className="text-lg md:text-xl font-display font-bold text-white relative z-60 pointer-events-auto">
                          <EditableText key={`scroll-title-${index}`} k={project.titleKey} as="span" />
                        </h3>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ReferencesSection;